import React, { useState, useEffect, useRef } from 'react';
import { Save, Trash2, RefreshCw, Search, X, Users, CheckCircle, Plus, Eye, Film, FileText, Music, Globe, Image, ArrowRight, Upload, Shield, TrendingUp, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import WebhooksManager from '../WebhooksManager';
import TotpSetup from '../TotpSetup';
import NumberCheckTab from './NumberCheckTab';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

// ערוצי מקור לצינתוק על חדשות חריגות (ברירת מחדל כשלא נשמרה בחירה)
const BREAKING_DEFAULT_CHANNELS = [
  { username: 'amitsegal', name: 'עמית סגל' },
  { username: 'hazfon1', name: 'חדשות לפני כולם' },
  { username: 'lelotsenzura', name: 'חדשות ללא צנזורה' }
];
const normChannel = (u) => String(u || '').replace(/^@/, '').toLowerCase();
const parseBreakingChannels = (raw) => {
  if (raw) {
    try {
      const list = JSON.parse(raw);
      if (Array.isArray(list)) return list.map(normChannel);
    } catch (e) { /* ברירת מחדל */ }
  }
  return BREAKING_DEFAULT_CHANNELS.map(c => c.username);
};

export default function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [gemini, setGemini] = useState({});
  const [videos, setVideos] = useState([]);
  const [videoSort, setVideoSort] = useState('date_desc');
  const [loading, setLoading] = useState(true);
  const [showSecrets, setShowSecrets] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [newsSummary, setNewsSummary] = useState({
    enabled: false,
    extension: 'ivr2:telegram/5',
    last_run: null,
    blackcat_enabled: false,
    hours: '',
    blackcat_hours: ''
  });

  const [searchModal, setSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addedChannels, setAddedChannels] = useState(new Set());
  const searchInputRef = useRef(null);
  const messagesListRef = useRef(null);

  const [previewModal, setPreviewModal] = useState(false);
  const [previewChannel, setPreviewChannel] = useState(null);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(new Set());
  const [playingMedia, setPlayingMedia] = useState({});
  const [driveUploading, setDriveUploading] = useState(new Set());
  const googleTokenRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [adminExtBuilding, setAdminExtBuilding] = useState(false);
  const [geminiTtsDefaults, setGeminiTtsDefaults] = useState(null);
  const [geminiTtsTestText, setGeminiTtsTestText] = useState('');
  const [geminiTtsAudio, setGeminiTtsAudio] = useState(null);
  const [geminiTtsBusy, setGeminiTtsBusy] = useState(null); // 'test' | 'design' | null
  const [allChannels, setAllChannels] = useState([]);

  useEffect(() => {
    loadSettings();
    api.get('/channels').then(r => setAllChannels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    loadGeminiSettings();
    loadVideos();
    loadGeminiTtsDefaults();
  }, []);

  const loadGeminiTtsDefaults = async () => {
    try {
      const response = await api.get('/settings/gemini-tts/defaults');
      setGeminiTtsDefaults(response.data);
    } catch (error) {
      console.error('שגיאה בטעינת ברירות המחדל של Gemini TTS', error);
    }
  };

  // שדות Gemini TTS הנוכחיים בפאנל (גם אם עוד לא נשמרו)
  const geminiTtsPayload = () => ({
    gemini_tts_api_key: settings.gemini_tts_api_key,
    gemini_tts_model: settings.gemini_tts_model,
    gemini_tts_voice_mode: settings.gemini_tts_voice_mode,
    gemini_tts_voice: settings.gemini_tts_voice,
    gemini_tts_voice_prompt: settings.gemini_tts_voice_prompt,
    gemini_tts_style: settings.gemini_tts_style,
  });

  const handleGeminiTtsTest = async () => {
    setGeminiTtsBusy('test');
    try {
      const res = await api.post('/settings/gemini-tts/test', { ...geminiTtsPayload(), text: geminiTtsTestText });
      setGeminiTtsAudio({ src: `data:${res.data.mime_type};base64,${res.data.audio}`, label: `דוגמה בקול ${res.data.voice}` });
      if (res.data.voice_expires) {
        // הבדיקה אולי עיצבה/חידשה קול - מעדכנים רק את שדות הקול, בלי לדרוס שינויים שלא נשמרו
        setSettings((prev) => ({ ...prev, gemini_tts_voice_id: res.data.voice, gemini_tts_voice_expires: res.data.voice_expires }));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה ביצירת דוגמה');
    } finally {
      setGeminiTtsBusy(null);
    }
  };

  const handleGeminiTtsDesign = async () => {
    if (!window.confirm('לעצב קול חדש מהתיאור? הקול הנוכחי יוחלף (כל עיצוב יוצא מעט שונה).')) return;
    setGeminiTtsBusy('design');
    try {
      const res = await api.post('/settings/gemini-tts/design-voice', geminiTtsPayload());
      if (res.data.sample_audio) {
        setGeminiTtsAudio({ src: `data:${res.data.sample_mime_type};base64,${res.data.sample_audio}`, label: 'דוגמת האודישן של הקול החדש' });
      }
      toast.success('קול חדש עוצב ונשמר');
      setSettings((prev) => ({
        ...prev,
        gemini_tts_voice_mode: 'designed',
        gemini_tts_voice_id: res.data.voice_id,
        gemini_tts_voice_expires: res.data.expire_time,
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בעיצוב הקול');
    } finally {
      setGeminiTtsBusy(null);
    }
  };

  const getGoogleToken = () => new Promise((resolve, reject) => {
    const tok = googleTokenRef.current;
    if (tok && tok.expires > Date.now()) return resolve(tok.access_token);

    const redirectUri = `${window.location.origin}/api/auth/google/callback`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: DRIVE_SCOPE,
      access_type: 'online',
      prompt: 'consent',
    });

    const popup = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      'google_drive_auth',
      'width=520,height=620,left=200,top=100'
    );

    if (!popup) return reject(new Error('חסימת popup — אפשר popups עבור האתר'));

    localStorage.removeItem('g_oauth_token');

    const pollTimer = setInterval(() => {
      const stored = localStorage.getItem('g_oauth_token');
      if (stored) {
        clearInterval(pollTimer);
        try {
          const { access_token, expires_in } = JSON.parse(stored);
          localStorage.removeItem('g_oauth_token');
          googleTokenRef.current = { access_token, expires: Date.now() + (expires_in || 3600) * 1000 };
          resolve(access_token);
        } catch (_) { reject(new Error('שגיאה בנתוני הטוקן')); }
        return;
      }
      if (popup.closed) {
        clearInterval(pollTimer);
        // One final check after popup closed
        setTimeout(() => {
          const lastCheck = localStorage.getItem('g_oauth_token');
          if (lastCheck) {
            try {
              const { access_token, expires_in } = JSON.parse(lastCheck);
              localStorage.removeItem('g_oauth_token');
              googleTokenRef.current = { access_token, expires: Date.now() + (expires_in || 3600) * 1000 };
              resolve(access_token);
            } catch (_) { reject(new Error('שגיאה בנתוני הטוקן')); }
          } else {
            reject(new Error('החלון נסגר לפני האישור'));
          }
        }, 500);
      }
    }, 300);
  });

  const uploadToDrive = async (msg) => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google Client ID לא מוגדר — הוסף VITE_GOOGLE_CLIENT_ID ל-.env');
      return;
    }
    setDriveUploading(prev => new Set([...prev, msg.id]));
    const progressToast = toast.loading('מתחבר לGoogle...');
    try {
      const token = await getGoogleToken();
      let blobUrl = playingMedia[msg.id];
      let blob;
      if (blobUrl) {
        toast.loading('מכין קובץ...', { id: progressToast });
        blob = await fetch(blobUrl).then(r => r.blob());
      } else {
        toast.loading('מוריד מטלגרם... (עשוי לקחת דקה)', { id: progressToast });
        const ref = previewChannel.username || String(previewChannel.id);
        const response = await api.get(
          `/channels/media?channel=${encodeURIComponent(ref)}&message_id=${msg.id}`,
          { responseType: 'blob' }
        );
        blob = response.data;
        const url = URL.createObjectURL(blob);
        setPlayingMedia(prev => ({ ...prev, [msg.id]: url }));
      }
      toast.loading('מעלה לDrive...', { id: progressToast });
      const filename = msg.file_name || `telegram_${msg.id}`;
      const metadata = JSON.stringify({ name: filename });
      const form = new FormData();
      form.append('metadata', new Blob([metadata], { type: 'application/json' }));
      form.append('file', blob, filename);
      const res = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink',
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
      );
      const data = await res.json();
      if (data.id) {
        toast.success(
          () => (
            <span>
              הועלה לדרייב!{' '}
              <a href={data.webViewLink} target="_blank" rel="noreferrer" className="underline font-bold">פתח</a>
            </span>
          ),
          { id: progressToast, duration: 6000 }
        );
      } else {
        toast.error('שגיאה בהעלאה לדרייב', { id: progressToast });
      }
    } catch (err) {
      toast.error(`שגיאה: ${err.message}`, { id: progressToast });
    } finally {
      setDriveUploading(prev => { const s = new Set(prev); s.delete(msg.id); return s; });
    }
  };

  const openSearchModal = () => {
    setSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
    setAddedChannels(new Set());
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const closeSearchModal = () => {
    setSearchModal(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleChannelSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const response = await api.get(`/channels/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast('לא נמצאו תוצאות', { icon: '🔍' });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בחיפוש ערוצים');
    } finally {
      setSearchLoading(false);
    }
  };

  const openPreviewModal = async (channel) => {
    setPreviewChannel(channel);
    setPreviewMessages([]);
    setPreviewModal(true);
    setPreviewLoading(true);
    try {
      const ref = channel.username || String(channel.id);
      const response = await api.get(`/channels/messages?channel=${encodeURIComponent(ref)}&limit=30`);
      setPreviewMessages(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בטעינת הודעות');
      setPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (!previewChannel || loadMoreLoading || previewMessages.length === 0) return;
    const oldestId = Math.min(...previewMessages.map(m => m.id));
    setLoadMoreLoading(true);
    try {
      const ref = previewChannel.username || String(previewChannel.id);
      const response = await api.get(`/channels/messages?channel=${encodeURIComponent(ref)}&limit=30&offset_id=${oldestId - 1}`);
      if (response.data.length === 0) {
        toast('אין הודעות ישנות יותר', { icon: '📭' });
      } else {
        const prevHeight = messagesListRef.current?.scrollHeight || 0;
        setPreviewMessages(prev => [...prev, ...response.data]);
        setTimeout(() => {
          if (messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight - prevHeight;
          }
        }, 50);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בטעינת הודעות');
    } finally {
      setLoadMoreLoading(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewChannel(null);
    setPreviewMessages([]);
    Object.values(playingMedia).forEach(url => URL.revokeObjectURL(url));
    setPlayingMedia({});
    setMediaLoading(new Set());
  };

  const handleLoadMedia = async (msg) => {
    if (!previewChannel || mediaLoading.has(msg.id)) return playingMedia[msg.id] || null;
    if (playingMedia[msg.id]) return playingMedia[msg.id];
    const ref = previewChannel.username || String(previewChannel.id);
    setMediaLoading(prev => new Set([...prev, msg.id]));
    try {
      const response = await api.get(
        `/channels/media?channel=${encodeURIComponent(ref)}&message_id=${msg.id}`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPlayingMedia(prev => ({ ...prev, [msg.id]: blobUrl }));
      return blobUrl;
    } catch (error) {
      toast.error('שגיאה בטעינת הקובץ');
      return null;
    } finally {
      setMediaLoading(prev => { const s = new Set(prev); s.delete(msg.id); return s; });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (secs) => {
    if (!secs) return '';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleAddChannel = async (channel) => {
    try {
      await api.post('/channels', {
        username: channel.username || String(channel.id),
        name: channel.title,
        category: channel.type === 'channel' ? 'ערוץ חדשות' : 'קבוצה',
        enabled: true,
        publish_to_general: true,
        publish_to_phone: true,
        publish_to_personal: false,
        send_videos_to_personal: false,
        send_to_admin_only: false,
      });
      setAddedChannels(prev => new Set([...prev, channel.id]));
      toast.success(`הערוץ "${channel.title}" נוסף בהצלחה`);
    } catch (error) {
      const msg = error.response?.data?.error || 'שגיאה בהוספת ערוץ';
      toast.error(msg);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      
      // טעינת הגדרות סיכום חדשות
      setNewsSummary({
        enabled: response.data.news_summary_enabled || false,
        extension: response.data.news_summary_extension || 'ivr2:telegram/5',
        last_run: response.data.news_summary_last_run || null,
        blackcat_enabled: response.data.news_summary_blackcat_enabled === 1 || response.data.news_summary_blackcat_enabled === true,
        hours: response.data.news_summary_hours || '',
        blackcat_hours: response.data.news_summary_blackcat_hours || ''
      });
    } catch (error) {
      toast.error('שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  };

  const loadGeminiSettings = async () => {
    try {
      const response = await api.get('/settings/gemini');
      setGemini(response.data);
    } catch (error) {
      console.error('שגיאה בטעינת הגדרות Gemini');
    }
  };

  const loadVideos = async () => {
    try {
      const response = await api.get('/videos');
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('שגיאה בטעינת סרטונים');
    }
  };

  const handleSecretCodeChange = (code) => {
    setSecretCode(code);
    if (code === '50200') {
      setShowSecrets(true);
      toast.success('מצב תצוגת סיסמאות מופעל', { duration: 2000 });
    } else if (showSecrets && code !== '50200') {
      setShowSecrets(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/settings', settings);
      toast.success('הגדרות נשמרו בהצלחה');
      loadSettings();
    } catch (error) {
      toast.error('שגיאה בשמירת הגדרות');
    }
  };

  const handleBuildAdminExt = async () => {
    try {
      // שמירה קודם כדי שהבונה יעבוד עם ההגדרות העדכניות
      await api.put('/settings', settings);
      setAdminExtBuilding(true);
      const res = await api.post('/settings/admin-ext/build');
      const data = res.data || {};
      if (data.success) {
        toast.success(`שלוחות הניהול נבנו בהצלחה (${data.boards_built}/${data.channels_count} ערוצים)`);
      } else {
        toast.error(`בניית השלוחות נכשלה: ${data.error || 'שגיאה לא ידועה'}`);
      }
      loadSettings();
    } catch (error) {
      toast.error('שגיאה בבניית שלוחות הניהול');
    } finally {
      setAdminExtBuilding(false);
    }
  };

  const handleSaveGemini = async () => {
    try {
      await api.put('/settings/gemini', gemini);
      toast.success('הגדרות Gemini נשמרו');
      loadGeminiSettings();
    } catch (error) {
      toast.error('שגיאה בשמירת הגדרות Gemini');
    }
  };

  const handleSaveNewsSummary = async () => {
    try {
      await api.put('/settings', {
        ...settings,
        news_summary_enabled: newsSummary.enabled ? 1 : 0,
        news_summary_extension: newsSummary.extension,
        news_summary_blackcat_enabled: newsSummary.blackcat_enabled ? 1 : 0,
        news_summary_hours: newsSummary.hours,
        news_summary_blackcat_hours: newsSummary.blackcat_hours
      });
      toast.success('הגדרות סיכום חדשות נשמרו');
      loadSettings();
    } catch (error) {
      toast.error('שגיאה בשמירת הגדרות סיכום');
    }
  };

  const weekendFields = () => ({
    weekend_summary_enabled: settings.weekend_summary_enabled ? 1 : 0,
    weekend_summary_source: settings.weekend_summary_source || 'amitsegal',
    weekend_summary_keywords: settings.weekend_summary_keywords ?? 'סיכום',
    weekend_summary_hours: parseInt(settings.weekend_summary_hours) || 2,
    weekend_summary_ext: settings.weekend_summary_ext || '100'
  });

  const handleToggleWeekend = async () => {
    const enabled = !(settings.weekend_summary_enabled === 1 || settings.weekend_summary_enabled === true);
    try {
      await api.put('/settings', { ...weekendFields(), weekend_summary_enabled: enabled ? 1 : 0 });
      setSettings({ ...settings, weekend_summary_enabled: enabled ? 1 : 0 });
      toast.success(enabled ? 'סיכום מוצאי שבת/חג הופעל' : 'סיכום מוצאי שבת/חג כובה');
    } catch (error) {
      toast.error('שגיאה בשינוי מצב');
    }
  };

  const handleSaveWeekend = async () => {
    try {
      await api.put('/settings', weekendFields());
      toast.success('הגדרות סיכום מוצאי שבת/חג נשמרו');
    } catch (error) {
      toast.error('שגיאה בשמירת ההגדרות');
    }
  };

  const [testingWeekend, setTestingWeekend] = useState(false);
  const handleTestWeekend = async () => {
    setTestingWeekend(true);
    try {
      const response = await api.post('/settings/weekend-summary/test', weekendFields());
      if (response.data.ok) toast.success(response.data.message);
      else toast.error(response.data.message, { duration: 7000 });
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בבדיקת החיבור', { duration: 7000 });
    } finally {
      setTestingWeekend(false);
    }
  };

  const handleRunSummaryNow = async () => {
    try {
      toast.loading('מייצר סיכום חדשות...');
      await api.post('/settings/news-summary/run');
      toast.dismiss();
      toast.success('סיכום חדשות הועלה בהצלחה');
      loadSettings();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.error || 'שגיאה ביצירת סיכום');
    }
  };

  const handleDeleteVideo = async (filename) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק סרטון זה?')) {
      return;
    }

    try {
      await api.delete(`/videos/${filename}`);
      toast.success('סרטון נמחק');
      loadVideos();
    } catch (error) {
      toast.error('שגיאה במחיקת סרטון');
    }
  };

  const handleCleanupVideos = async (days) => {
    if (!window.confirm(`האם למחוק סרטונים מעל ${days} ימים?`)) {
      return;
    }

    try {
      const response = await api.delete(`/videos/cleanup/old?days=${days}`);
      toast.success(response.data.message);
      loadVideos();
    } catch (error) {
      toast.error('שגיאה במחיקת סרטונים');
    }
  };

  const handleCleanupAll = async () => {
    if (!window.confirm('האם למחוק את כל הסרטונים? פעולה זו בלתי הפיכה!')) {
      return;
    }

    try {
      const response = await api.delete('/videos/cleanup/all');
      toast.success(response.data.message);
      loadVideos();
    } catch (error) {
      toast.error('שגיאה במחיקת סרטונים');
    }
  };

  const sortedVideos = React.useMemo(() => {
    const items = [...videos];

    switch (videoSort) {
      case 'date_asc':
        return items.sort((a, b) => new Date(a.created) - new Date(b.created));
      case 'size_desc':
        return items.sort((a, b) => b.size - a.size);
      case 'size_asc':
        return items.sort((a, b) => a.size - b.size);
      case 'date_desc':
      default:
        return items.sort((a, b) => new Date(b.created) - new Date(a.created));
    }
  }, [videos, videoSort]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="text-center py-12">טוען הגדרות...</div>;
  }

  return (
    <div className="space-y-6">

      {/* מודל חיפוש ערוצים */}
      {searchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-600" />
                חיפוש ערוצים בטלגרם
              </h2>
              <button onClick={closeSearchModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search bar */}
            <form onSubmit={handleChannelSearch} className="p-4 border-b flex gap-2">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="הכנס שם ערוץ, קבוצה או נושא..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-right"
                dir="auto"
              />
              <button
                type="submit"
                disabled={searchLoading || searchQuery.trim().length < 2}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {searchLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                חפש
              </button>
            </form>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {searchLoading && (
                <div className="text-center py-10 text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-500" />
                  <p>מחפש ערוצים...</p>
                </div>
              )}

              {!searchLoading && searchResults.length === 0 && searchQuery && (
                <div className="text-center py-10 text-gray-400">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>לא נמצאו תוצאות</p>
                </div>
              )}

              {!searchLoading && searchResults.length === 0 && !searchQuery && (
                <div className="text-center py-10 text-gray-400">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">הכנס טקסט חיפוש כדי למצוא ערוצים</p>
                </div>
              )}

              {searchResults.map((ch) => (
                <div key={ch.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  {/* Avatar - click to preview */}
                  <button
                    onClick={() => openPreviewModal(ch)}
                    className="flex-shrink-0 w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg hover:bg-blue-200 transition-colors"
                    title="צפה בהודעות"
                  >
                    {ch.title.charAt(0).toUpperCase()}
                  </button>

                  {/* Info - click to preview */}
                  <button
                    onClick={() => openPreviewModal(ch)}
                    className="flex-1 min-w-0 text-right hover:opacity-80 transition-opacity"
                  >
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{ch.title}</span>
                      {ch.verified && (
                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" title="ערוץ מאומת" />
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        ch.type === 'channel' ? 'bg-blue-100 text-blue-700' :
                        ch.type === 'supergroup' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {ch.type === 'channel' ? 'ערוץ' : ch.type === 'supergroup' ? 'סופרגרופ' : 'קבוצה'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
                      {ch.username && (
                        <span dir="ltr" className="font-mono">@{ch.username}</span>
                      )}
                      {ch.members_count > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {ch.members_count.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Action buttons */}
                  <div className="flex-shrink-0 flex gap-1">
                    <button
                      onClick={() => openPreviewModal(ch)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                      title="צפה בהודעות"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAddChannel(ch)}
                      disabled={addedChannels.has(ch.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        addedChannels.has(ch.id)
                          ? 'bg-green-100 text-green-700 cursor-default'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {addedChannels.has(ch.id) ? (
                        <><CheckCircle className="w-4 h-4" /> נוסף</>
                      ) : (
                        <><Plus className="w-4 h-4" /> הוסף</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-4 border-t text-xs text-gray-400 text-center">
              התוצאות מגיעות ישירות מטלגרם דרך חשבון המשתמש המחובר
            </div>
          </div>
        </div>
      )}

      {/* מודל תצוגת הודעות ערוץ */}
      {previewModal && previewChannel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b bg-gray-50 rounded-t-xl">
              <button
                onClick={closePreviewModal}
                className="text-gray-400 hover:text-gray-700 transition-colors"
                title="חזור לחיפוש"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                {previewChannel.title.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-gray-900 truncate">{previewChannel.title}</span>
                  {previewChannel.verified && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                </div>
                {previewChannel.username && (
                  <span className="text-xs text-gray-500 font-mono" dir="ltr">@{previewChannel.username}</span>
                )}
              </div>
              <button
                onClick={() => { handleAddChannel(previewChannel); }}
                disabled={addedChannels.has(previewChannel.id)}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  addedChannels.has(previewChannel.id)
                    ? 'bg-green-100 text-green-700 cursor-default'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {addedChannels.has(previewChannel.id) ? <><CheckCircle className="w-4 h-4" /> נוסף</> : <><Plus className="w-4 h-4" /> הוסף</>}
              </button>
              <button onClick={closePreviewModal} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages list */}
            <div ref={messagesListRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f0f2f5]">
              {previewLoading && (
                <div className="text-center py-16">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                  <p className="text-gray-500">טוען הודעות... (עשוי לקחת מספר שניות)</p>
                </div>
              )}

              {!previewLoading && previewMessages.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>אין הודעות להצגה</p>
                </div>
              )}

              {previewMessages.map((msg) => (
                <div key={msg.id} className="bg-white rounded-xl shadow-sm p-3 max-w-[90%] ml-auto">

                  {/* Photo */}
                  {msg.media_type === 'photo' && msg.media_data && (
                    <div className="mb-2 rounded-lg overflow-hidden bg-gray-100 cursor-zoom-in"
                      onClick={() => {
                        const ref = previewChannel.username || String(previewChannel.id);
                        setLightbox({ thumb: `data:image/jpeg;base64,${msg.media_data}`, full: null, loading: true });
                        api.get(`/channels/media?channel=${encodeURIComponent(ref)}&message_id=${msg.id}`, { responseType: 'blob' })
                          .then(r => setLightbox(prev => prev ? { ...prev, full: URL.createObjectURL(r.data), loading: false } : null))
                          .catch(() => setLightbox(prev => prev ? { ...prev, loading: false } : null));
                      }}
                    >
                      <img
                        src={`data:image/jpeg;base64,${msg.media_data}`}
                        alt=""
                        className="w-full max-h-72 object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Video with thumbnail → inline player after load */}
                  {msg.media_type === 'video' && (
                    <div className="mb-2 rounded-lg overflow-hidden bg-gray-900">
                      {playingMedia[msg.id] ? (
                        <video
                          src={playingMedia[msg.id]}
                          controls
                          autoPlay
                          className="w-full max-h-72"
                        />
                      ) : (
                        <button
                          onClick={() => handleLoadMedia(msg)}
                          disabled={mediaLoading.has(msg.id)}
                          className="relative w-full focus:outline-none"
                        >
                          {msg.media_data ? (
                            <img
                              src={`data:image/jpeg;base64,${msg.media_data}`}
                              alt=""
                              className="w-full max-h-64 object-contain opacity-75"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-32 bg-gray-800">
                              <Film className="w-10 h-10 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                            {mediaLoading.has(msg.id) ? (
                              <RefreshCw className="w-9 h-9 text-white animate-spin" />
                            ) : (
                              <>
                                <div className="bg-black bg-opacity-60 rounded-full p-3 hover:bg-opacity-80 transition-all">
                                  <Film className="w-7 h-7 text-white" />
                                </div>
                                <span className="text-white text-xs bg-black bg-opacity-50 px-2 py-0.5 rounded">
                                  {msg.file_size ? formatFileSize(msg.file_size) : ''}{msg.duration ? ` · ${formatDuration(msg.duration)}` : ''} — לחץ להפעלה
                                </span>
                              </>
                            )}
                          </div>
                          {msg.duration && !mediaLoading.has(msg.id) && (
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-1.5 py-0.5 rounded">
                              {formatDuration(msg.duration)}
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Drive button for videos */}
                  {msg.media_type === 'video' && GOOGLE_CLIENT_ID && (
                    <div className="mb-1 flex justify-end">
                      <button
                        onClick={() => uploadToDrive(msg)}
                        disabled={driveUploading.has(msg.id) || mediaLoading.has(msg.id)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60"
                      >
                        {driveUploading.has(msg.id)
                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                          : <Upload className="w-3 h-3" />
                        }
                        העלה לDrive
                      </button>
                    </div>
                  )}

                  {/* GIF inline player */}
                  {msg.media_type === 'gif' && (
                    <div className="mb-2 rounded-lg overflow-hidden bg-gray-100">
                      {playingMedia[msg.id] ? (
                        <video src={playingMedia[msg.id]} autoPlay loop muted className="w-full max-h-64" />
                      ) : msg.media_data ? (
                        <button onClick={() => handleLoadMedia(msg)} disabled={mediaLoading.has(msg.id)} className="relative w-full">
                          <img src={`data:image/jpeg;base64,${msg.media_data}`} alt="" className="w-full max-h-64 object-contain" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            {mediaLoading.has(msg.id)
                              ? <RefreshCw className="w-7 h-7 text-white animate-spin" />
                              : <span className="bg-black bg-opacity-60 text-white text-xs font-bold px-3 py-1 rounded-full hover:bg-opacity-80">GIF ▶</span>
                            }
                          </div>
                        </button>
                      ) : null}
                    </div>
                  )}

                  {/* Audio → inline player */}
                  {msg.media_type === 'audio' && (
                    <div className="mb-2 bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Music className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{msg.file_name || 'קובץ שמע'}</div>
                          <div className="text-xs text-gray-500">
                            {msg.duration ? formatDuration(msg.duration) : ''}
                            {msg.file_size ? ` · ${formatFileSize(msg.file_size)}` : ''}
                          </div>
                        </div>
                        {!playingMedia[msg.id] && (
                          <button
                            onClick={() => handleLoadMedia(msg)}
                            disabled={mediaLoading.has(msg.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-60"
                          >
                            {mediaLoading.has(msg.id) ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : '▶ הפעל'}
                          </button>
                        )}
                      </div>
                      {playingMedia[msg.id] && (
                        <audio src={playingMedia[msg.id]} controls autoPlay className="w-full" />
                      )}
                    </div>
                  )}

                  {/* Document → download */}
                  {msg.media_type === 'document' && (
                    <div className="mb-2 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{msg.file_name || 'קובץ'}</div>
                        <div className="text-xs text-gray-500">
                          {msg.mime_type || ''}{msg.file_size ? ` · ${formatFileSize(msg.file_size)}` : ''}
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-1.5">
                        <button
                          onClick={async () => {
                            const url = await handleLoadMedia(msg);
                            if (url) {
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = msg.file_name || 'file';
                              a.click();
                            }
                          }}
                          disabled={mediaLoading.has(msg.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 disabled:opacity-60"
                        >
                          {mediaLoading.has(msg.id)
                            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            : playingMedia[msg.id] ? '⬇ שמור' : '⬇ הורד'
                          }
                        </button>
                        {GOOGLE_CLIENT_ID && (
                          <button
                            onClick={() => uploadToDrive(msg)}
                            disabled={driveUploading.has(msg.id) || mediaLoading.has(msg.id)}
                            title="העלה לGoogle Drive"
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60"
                          >
                            {driveUploading.has(msg.id)
                              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              : <Upload className="w-3.5 h-3.5" />
                            }
                            Drive
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Webpage preview */}
                  {msg.media_type === 'webpage' && msg.webpage_url && (
                    <div className="mb-2 border-r-4 border-blue-400 pr-3 bg-blue-50 rounded-r-lg py-2">
                      <div className="text-xs text-blue-600 truncate" dir="ltr">{msg.webpage_url}</div>
                      {msg.webpage_title && (
                        <div className="text-sm font-medium text-gray-800 mt-0.5">{msg.webpage_title}</div>
                      )}
                      {msg.webpage_description && (
                        <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{msg.webpage_description}</div>
                      )}
                    </div>
                  )}

                  {/* Text */}
                  {msg.text && (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words" dir="auto">{msg.text}</p>
                  )}

                  {/* Meta */}
                  <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                    <span>
                      {msg.date ? new Date(msg.date).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                    {msg.views > 0 && (
                      <span>{msg.views.toLocaleString()} 👁</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
              <span className="text-xs text-gray-400">מציג {previewMessages.length} הודעות</span>
              <button
                onClick={loadMoreMessages}
                disabled={loadMoreLoading || previewLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {loadMoreLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                טען 30 הודעות ישנות יותר
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setLightbox(null)}>
            <X className="w-8 h-8" />
          </button>
          {lightbox.loading && (
            <div className="absolute top-4 left-4 flex items-center gap-2 text-white text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> טוען באיכות מלאה...
            </div>
          )}
          <img
            src={lightbox.full || lightbox.thumb}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* הגדרות כללי */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">הגדרות כללי (API)</h2>
          <div className="flex gap-2">
            <button
              onClick={openSearchModal}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Search className="w-5 h-5 ml-2" />
              חיפוש ערוצים
            </button>
            <button
              onClick={handleSaveSettings}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-5 h-5 ml-2" />
              שמור
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              כתובת API אישי
            </label>
            <input
              type="url"
              value={settings.personal_api_url || ''}
              onChange={(e) => setSettings({ ...settings, personal_api_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/api/news_update.php"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              תאריך סיום מנוי
            </label>
            <input
              type="date"
              value={settings.subscription_end_date || ''}
              onChange={(e) => setSettings({ ...settings, subscription_end_date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              יוצג בכותרת הראשית — ספירת ימים לסיום
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {/* שליחת הודעות שנערכו למנהלים */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.send_edited_to_admin === 1 || settings.send_edited_to_admin === true || settings.send_edited_to_admin === undefined}
              onChange={(e) => setSettings({ ...settings, send_edited_to_admin: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-900">שליחת הודעות שנערכו למנהלים</span>
              <p className="text-sm text-gray-500">כאשר מופעל — הודעות שנערכו בערוצים יישלחו לפאנל המנהלים. כאשר כבוי — עריכות יתעלמו לחלוטין</p>
            </div>
          </label>
        </div>
      </div>

      {/* הגדרות ימות המשיח */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">הגדרות ימות המשיח</h2>
          <div className="flex gap-2">
            <input
              type="password"
              value={secretCode}
              onChange={(e) => handleSecretCodeChange(e.target.value)}
              placeholder="קוד תצוגה"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
              style={{ width: '120px' }}
            />
            <button
              onClick={handleSaveSettings}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Save className="w-5 h-5 ml-2" />
              שמור
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              פרטי התחברות ימות (פורמט: username:password)
            </label>
            <input
              type={showSecrets ? "text" : "password"}
              value={showSecrets ? `${settings.yemot_username || ''}:${settings.yemot_password || ''}` : (settings.yemot_username ? `${settings.yemot_username}:••••••••••••` : '')}
              onChange={(e) => {
                const parts = e.target.value.split(':');
                setSettings({ 
                  ...settings, 
                  yemot_username: parts[0] || '',
                  yemot_password: parts[1] || ''
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
              placeholder="0747175868:5020050200"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yemot Token
            </label>
            <input
              type={showSecrets ? "text" : "password"}
              value={settings.yemot_token || ''}
              onChange={(e) => setSettings({ ...settings, yemot_token: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
              dir="ltr"
              placeholder="הכנס טוקן ימות קבוע"
            />
            <p className="mt-1 text-xs text-gray-500">טוקן קבוע לחיבור לימות המשיח</p>
          </div>

          {/* שליחת סרטונים לימות */}
          <div className="col-span-1 md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.yemot_send_videos === 1 || settings.yemot_send_videos === true}
                onChange={(e) => setSettings({ ...settings, yemot_send_videos: e.target.checked ? 1 : 0 })}
                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="font-medium text-gray-700">שליחת סרטונים לימות המשיח</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 mr-8">
              כשמופעל, סרטונים מהודעות טלגרם יומרו ויועלו לשלוחה המתאימה בימות (הקישו 7 לצפייה)
            </p>
          </div>

          {/* בחירת ספק TTS */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ספק TTS (המרת טקסט לדיבור)
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tts_provider"
                  value="google"
                  checked={(settings.tts_provider || 'google') === 'google'}
                  onChange={(e) => setSettings({ ...settings, tts_provider: e.target.value })}
                  className="ml-2"
                />
                <span>Google TTS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tts_provider"
                  value="openai"
                  checked={settings.tts_provider === 'openai'}
                  onChange={(e) => setSettings({ ...settings, tts_provider: e.target.value })}
                  className="ml-2"
                />
                <span>OpenAI TTS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tts_provider"
                  value="yemot"
                  checked={settings.tts_provider === 'yemot'}
                  onChange={(e) => setSettings({ ...settings, tts_provider: e.target.value })}
                  className="ml-2"
                />
                <span>ימות המשיח TTS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="tts_provider"
                  value="gemini"
                  checked={settings.tts_provider === 'gemini'}
                  onChange={(e) => setSettings({ ...settings, tts_provider: e.target.value })}
                  className="ml-2"
                />
                <span>Gemini TTS</span>
              </label>
            </div>
          </div>

          {/* הגדרות Google TTS */}
          {(settings.tts_provider || 'google') === 'google' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TTS API Key (Google)
                </label>
                <input
                  type={showSecrets ? "text" : "password"}
                  value={showSecrets ? (settings.tts_api_key || '') : (settings.tts_api_key ? '••••••••••••' : '')}
                  onChange={(e) => setSettings({ ...settings, tts_api_key: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
                  dir="ltr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TTS Voice (Google)
                </label>
                <input
                  type="text"
                  value={settings.tts_voice || 'he-IL-Standard-D'}
                  onChange={(e) => setSettings({ ...settings, tts_voice: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TTS Pitch ({settings.tts_pitch || -5.6})
                </label>
                <input
                  type="range"
                  min="-20"
                  max="20"
                  step="0.1"
                  value={settings.tts_pitch || -5.6}
                  onChange={(e) => setSettings({ ...settings, tts_pitch: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TTS Speed ({settings.tts_speed || 1.15})
                </label>
                <input
                  type="range"
                  min="0.25"
                  max="4.0"
                  step="0.05"
                  value={settings.tts_speed || 1.15}
                  onChange={(e) => setSettings({ ...settings, tts_speed: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>
            </>
          )}

          {/* הסבר TTS ימות */}
          {settings.tts_provider === 'yemot' && (
            <div className="col-span-1 md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>TTS ימות המשיח:</strong> הטקסט יועלה ישירות למערכת ימות המשיח כקובץ טקסט, 
                וימות ימיר אותו לדיבור אוטומטית. לא נדרש מפתח API חיצוני - המערכת משתמשת בפרטי ההתחברות של ימות שהוגדרו למעלה.
              </p>
            </div>
          )}

          {/* הגדרות OpenAI TTS */}
          {settings.tts_provider === 'openai' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenAI Voice
                </label>
                <select
                  value={settings.openai_voice || 'echo'}
                  onChange={(e) => setSettings({ ...settings, openai_voice: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="echo">Echo</option>
                  <option value="alloy">Alloy</option>
                  <option value="fable">Fable</option>
                  <option value="onyx">Onyx</option>
                  <option value="nova">Nova</option>
                  <option value="shimmer">Shimmer</option>
                </select>
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenAI Prompt (הנחיות דיבור)
                </label>
                <textarea
                  value={settings.openai_prompt || ''}
                  onChange={(e) => setSettings({ ...settings, openai_prompt: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows="4"
                  placeholder="Voice: Clear, authoritative, and composed..."
                  dir="ltr"
                />
                <p className="mt-1 text-xs text-gray-500">
                  הגדר את סגנון הדיבור, הטון והקצב של הקריינות
                </p>
              </div>
            </>
          )}

          {/* הגדרות Gemini TTS */}
          {settings.tts_provider === 'gemini' && (
            <>
              <div className="col-span-1 md:col-span-2 bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-900 space-y-1">
                <p>
                  <strong>Gemini TTS:</strong> קריין חדשות עם הוראות משחק. הטקסט מוקרא מילה במילה, ו"איך להקריא" נקבע בהוראות המשחק למטה.
                </p>
                <p className="text-xs">
                  מסלול חינמי: 3 בקשות לדקה ו-10 ליום, כלומר מתאים רק לניסוי. בתשלום: כ-25 טוקנים לשנייה של שמע.
                  אם הבקשה נכשלת (למשל בגלל המכסה), המערכת יכולה לחזור ל-Google TTS.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gemini API Key (TTS)
                </label>
                <input
                  type={showSecrets ? "text" : "password"}
                  value={showSecrets ? (settings.gemini_tts_api_key || '') : (settings.gemini_tts_api_key ? '••••••••••••' : '')}
                  onChange={(e) => setSettings({ ...settings, gemini_tts_api_key: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 font-mono"
                  placeholder="ריק = ההרשאה מלשונית Gemini (API key או Vertex)"
                  dir="ltr"
                />
                <p className="mt-1 text-xs text-gray-500">
                  אפשר להזין API key של AI Studio (או כמה, מופרדים בפסיקים), או JSON של service account מ-Vertex AI.
                  אם השדה ריק, המערכת משתמשת בהרשאה של הסינון, ואם היא נכשלת, ב-GEMINI_API_KEY מקובץ ה-.env.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מודל
                </label>
                <select
                  value={settings.gemini_tts_model || 'gemini-3.8-flash-tts'}
                  onChange={(e) => setSettings({ ...settings, gemini_tts_model: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  dir="ltr"
                >
                  {(geminiTtsDefaults?.models || ['gemini-3.8-flash-tts', 'gemini-3.8-flash-lite-tts', 'gemini-3.1-flash-tts-preview', 'gemini-2.5-flash-tts']).map((m) => (
                    <option key={m} value={m}>{m}{m.startsWith('gemini-3.8') ? '' : ' (דור קודם)'}</option>
                  ))}
                </select>
                {!(settings.gemini_tts_model || 'gemini-3.8-flash-tts').startsWith('gemini-3.8') && (
                  <p className="mt-1 text-xs text-amber-700">
                    דור קודם: זמין ב-Vertex, אבל בלי קול מעוצב. הוראות המשחק נשלחות כפרומפט לפני הטקסט.
                    כש-3.8 יגיע לפרויקט ב-Vertex, כדאי לעבור אליו.
                  </p>
                )}
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סוג קול
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gemini_tts_voice_mode"
                      value="prebuilt"
                      checked={(settings.gemini_tts_voice_mode || 'prebuilt') === 'prebuilt'}
                      onChange={(e) => setSettings({ ...settings, gemini_tts_voice_mode: e.target.value })}
                      className="ml-2"
                    />
                    <span>קול מוכן (30 קולות)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gemini_tts_voice_mode"
                      value="designed"
                      checked={settings.gemini_tts_voice_mode === 'designed'}
                      onChange={(e) => setSettings({ ...settings, gemini_tts_voice_mode: e.target.value })}
                      disabled={!(settings.gemini_tts_model || 'gemini-3.8-flash-tts').startsWith('gemini-3.8')}
                      className="ml-2"
                    />
                    <span>קול מעוצב מתיאור (Voice Design)</span>
                  </label>
                </div>
              </div>

              {((settings.gemini_tts_voice_mode || 'prebuilt') === 'prebuilt' || !(settings.gemini_tts_model || 'gemini-3.8-flash-tts').startsWith('gemini-3.8')) ? (
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    קול
                  </label>
                  <select
                    value={settings.gemini_tts_voice || 'Charon'}
                    onChange={(e) => setSettings({ ...settings, gemini_tts_voice: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                    dir="ltr"
                  >
                    {Object.entries(geminiTtsDefaults?.voices || { Charon: 'Informative' }).map(([name, desc]) => (
                      <option key={name} value={name}>{name} - {desc}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    אין בספרייה קולות עבריים ייעודיים, אבל כל הקולות מדברים עברית. לחדשות מתאימים Charon, Rasalgethi, Sadaltager ו-Fenrir.
                  </p>
                </div>
              ) : (
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    תיאור הקול (באנגלית, משפט או שניים: גיל, מגדר, גוון, מבטא)
                  </label>
                  <textarea
                    value={settings.gemini_tts_voice_prompt || ''}
                    onChange={(e) => setSettings({ ...settings, gemini_tts_voice_prompt: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                    rows="3"
                    placeholder={geminiTtsDefaults?.voice_prompt}
                    dir="ltr"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleGeminiTtsDesign}
                      disabled={geminiTtsBusy !== null}
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-60 text-sm"
                    >
                      {geminiTtsBusy === 'design' ? 'מעצב...' : '🎨 עצב קול חדש'}
                    </button>
                    <span className="text-xs text-gray-500">
                      {settings.gemini_tts_voice_id
                        ? `קול פעיל: ${settings.gemini_tts_voice_id} · בתוקף עד ${new Date(settings.gemini_tts_voice_expires).toLocaleString('he-IL')}`
                        : 'עוד לא עוצב קול. הוא ייווצר בשימוש הראשון.'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    גיל ומבטא נכנסים לתיאור הקול, לא להוראות המשחק. כל עיצוב יוצא מעט שונה, אז כדאי לנסות כמה פעמים ולבחור באוזן.
                    גוגל שומרת קול מעוצב 7 ימים בלבד. לפני שהוא פג המערכת מעצבת אותו מחדש אוטומטית מאותו תיאור, והקול עשוי להשתנות מעט.
                    תיאור של קול ילד נחסם על ידי גוגל.
                  </p>
                </div>
              )}

              <div className="col-span-1 md:col-span-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    הוראות משחק לקריין (style)
                  </label>
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, gemini_tts_style: geminiTtsDefaults?.style || '' })}
                    className="text-xs text-sky-700 hover:underline"
                  >
                    טען ברירת מחדל
                  </button>
                </div>
                <textarea
                  value={settings.gemini_tts_style || ''}
                  onChange={(e) => setSettings({ ...settings, gemini_tts_style: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 font-mono text-sm"
                  rows="7"
                  placeholder={geminiTtsDefaults?.style}
                  dir="ltr"
                />
                <p className="mt-1 text-xs text-gray-500">
                  ריק = ברירת המחדל: קריין חדשות סוחף, דרמטי ומעניין, בקצב רץ ונעים. אל תכניסו הוראות לתוך הטקסט עצמו, כי הוא מוקרא מילה במילה.
                </p>
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Number(settings.gemini_tts_split) === 1 || settings.gemini_tts_split === true}
                    onChange={(e) => setSettings({ ...settings, gemini_tts_split: e.target.checked ? 1 : 0 })}
                    className="w-5 h-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-gray-700">פירוק למשפטים: הוראת משחק לכל חלק (פתיחה, אמצע, סיום)</span>
                </label>
                <p className="text-xs text-gray-500 mr-8">
                  פסקה שלמה נוטה לצאת בטון אחד. פירוק נותן קריינות חיה יותר, אבל כל חלק הוא בקשה נפרדת (עד 6 להודעה), ולכן הוא צורך יותר מהמכסה.
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.gemini_tts_fallback === undefined || settings.gemini_tts_fallback === null || Number(settings.gemini_tts_fallback) === 1 || settings.gemini_tts_fallback === true}
                    onChange={(e) => setSettings({ ...settings, gemini_tts_fallback: e.target.checked ? 1 : 0 })}
                    className="w-5 h-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-gray-700">אם Gemini נכשל, להשתמש ב-Google TTS (דורש מפתח Google TTS)</span>
                </label>
              </div>

              <div className="col-span-1 md:col-span-2 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  בדיקת קול
                </label>
                <textarea
                  value={geminiTtsTestText}
                  onChange={(e) => setGeminiTtsTestText(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  rows="2"
                  placeholder={geminiTtsDefaults?.sample_text}
                />
                <p className="mt-1 text-xs text-gray-500">
                  אפשר להוסיף תגיות כמו &lt;short pause&gt;, &lt;sigh&gt; או &lt;laugh&gt;. הבדיקה משתמשת בהגדרות שבמסך, גם אם עוד לא נשמרו, וצורכת בקשה מהמכסה.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleGeminiTtsTest}
                    disabled={geminiTtsBusy !== null}
                    className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-60 text-sm"
                  >
                    {geminiTtsBusy === 'test' ? 'מייצר...' : '▶️ השמע דוגמה'}
                  </button>
                  {geminiTtsAudio && (
                    <div className="flex items-center gap-2">
                      <audio controls autoPlay src={geminiTtsAudio.src} />
                      <span className="text-xs text-gray-500">{geminiTtsAudio.label}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* בדיקת מספרים: קווים ישנים בימות שעדיין מפנים לקו שלנו (רץ בשרת ברקע, backend/routes/numberCheck.js) */}
      <NumberCheckTab />

      {/* שלוחת ניהול בטלפון (Admin phone extension) */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-2 border-indigo-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">☎️ שלוחת ניהול בטלפון</h2>
          <div className="flex gap-2">
            <button
              onClick={handleBuildAdminExt}
              disabled={adminExtBuilding}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
              title="בונה/מעדכן את עץ השלוחות בימות לפי הערוצים הפעילים"
            >
              <RefreshCw className={`w-5 h-5 ml-2 ${adminExtBuilding ? 'animate-spin' : ''}`} />
              {adminExtBuilding ? 'בונה...' : 'בנה / עדכן שלוחות'}
            </button>
            <button
              onClick={handleSaveSettings}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-5 h-5 ml-2" />
              שמור
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          שלוחה נפרדת למנהלים בלבד. מקבלת <b>את כל ההודעות מכל הערוצים הפעילים</b>. בכל שלוחה
          גם סרטונים (מקש 7). תפריט אחד בקריינות גוגל: <b>כוכבית</b> = כל ההודעות ללא כפילויות,
          <b>00</b> = כל ההודעות כולל כפילויות (מיידי), וספרות = הבורד של כל ערוץ (מיידי, ללא סינון).
          ההעלאה רצה בתהליך נפרד כדי לא להפריע לשלוחות הרגילות.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1 md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.admin_ext_enabled === 1 || settings.admin_ext_enabled === true}
                onChange={(e) => setSettings({ ...settings, admin_ext_enabled: e.target.checked ? 1 : 0 })}
                className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="font-medium text-gray-700">הפעל שלוחת ניהול</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 mr-8">
              כשמופעל, כל הודעה נכנסת נשלחת גם לשלוחת הניהול (דרך תור נפרד).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              שלוחת בסיס בימות
            </label>
            <input
              type="text"
              value={settings.admin_ext_base || 'telegram/7'}
              onChange={(e) => setSettings({ ...settings, admin_ext_base: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              dir="ltr"
              placeholder="telegram/7"
            />
            <p className="mt-1 text-xs text-gray-500">נתיב השלוחה שבה ייבנה עץ הניהול (ללא ivr2:)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              סיסמת כניסה
            </label>
            <input
              type="text"
              value={settings.admin_ext_password || '4343'}
              onChange={(e) => setSettings({ ...settings, admin_ext_password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              dir="ltr"
              placeholder="4343"
            />
            <p className="mt-1 text-xs text-gray-500">סיסמה לכניסה למי שאינו ברשימת התפוצה</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              רשימת תפוצה לכניסה ללא סיסמה
            </label>
            <input
              type="text"
              value={settings.admin_ext_whitelist_template || '5'}
              onChange={(e) => setSettings({ ...settings, admin_ext_whitelist_template: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              dir="ltr"
              placeholder="5"
            />
            <p className="mt-1 text-xs text-gray-500">מספר רשימת התפוצה (template) שחבריה נכנסים ללא סיסמה</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              קריין ההודעות
            </label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="admin_ext_msg_tts"
                  value="yemot"
                  checked={(settings.admin_ext_msg_tts || 'yemot') === 'yemot'}
                  onChange={(e) => setSettings({ ...settings, admin_ext_msg_tts: e.target.value })}
                  className="ml-2"
                />
                <span>ימות המשיח TTS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="admin_ext_msg_tts"
                  value="google"
                  checked={settings.admin_ext_msg_tts === 'google'}
                  onChange={(e) => setSettings({ ...settings, admin_ext_msg_tts: e.target.value })}
                  className="ml-2"
                />
                <span>Google TTS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="admin_ext_msg_tts"
                  value="gemini"
                  checked={settings.admin_ext_msg_tts === 'gemini'}
                  onChange={(e) => setSettings({ ...settings, admin_ext_msg_tts: e.target.value })}
                  className="ml-2"
                />
                <span>Gemini TTS</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">קריין ההודעות בבורדים (התפריט תמיד בגוגל). Gemini משתמש בהגדרות ה-Gemini TTS שלמעלה.</p>
          </div>

          <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.admin_ext_publish_enabled === undefined || settings.admin_ext_publish_enabled === null || settings.admin_ext_publish_enabled === 1 || settings.admin_ext_publish_enabled === true}
                onChange={(e) => setSettings({ ...settings, admin_ext_publish_enabled: e.target.checked ? 1 : 0 })}
                className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="font-medium text-gray-700">מקש 9 בזמן האזנה: עריכה ופרסום כהודעה זמנית</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 mr-8">
              בזמן שמושמעת הודעה, מקש 9 מתחיל דיאלוג: שמירה כמו שהיא או עריכה בהקלטה (עם תמלול), כמה שעות, עם צינתוק או בלי.
              אחרי שינוי יש ללחוץ "בנה / עדכן שלוחות".
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סיסמה למקש 9</label>
            <input
              type="text"
              inputMode="numeric"
              value={settings.admin_ext_publish_password ?? '33720'}
              onChange={(e) => setSettings({ ...settings, admin_ext_publish_password: e.target.value.replace(/[^0-9]/g, '').slice(0, 8) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
              placeholder="33720"
              dir="ltr"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500">ספרות בלבד (עד 8). נדרשת בכל לחיצה על 9, לפני עריכה או פרסום. 5 ניסיונות שגויים = חסימה ל-15 דקות.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סיסמה מצומצמת למקש 9</label>
            <input
              type="text"
              inputMode="numeric"
              value={settings.admin_ext_publish_password_limited ?? '50280'}
              onChange={(e) => setSettings({ ...settings, admin_ext_publish_password_limited: e.target.value.replace(/[^0-9]/g, '').slice(0, 8) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
              placeholder="50280"
              dir="ltr"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500">עם הסיסמה הזו אפשר רק לפרסם את ההודעה שלחצו עליה בפתיח לשעה אחת: בלי עריכה, בלי בחירת זמן ובלי צינתוק, עם אישור או ביטול.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">רשימת צינתוקים</label>
            <input
              type="text"
              value={settings.admin_ext_tzintuk_list ?? 'חדש'}
              onChange={(e) => setSettings({ ...settings, admin_ext_tzintuk_list: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="חדש"
            />
            <p className="mt-1 text-xs text-gray-500">שם או מספר הרשימה בימות (כרגע: חדש).</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">מספר מתקשר (Caller ID) לצינתוק</label>
            <input
              type="text"
              value={settings.admin_ext_tzintuk_caller ?? '0747175868'}
              onChange={(e) => setSettings({ ...settings, admin_ext_tzintuk_caller: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="0747175868"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">שלוחת ההודעה הזמנית (העדכונים)</label>
            <input
              type="text"
              value={settings.admin_ext_update_ext ?? '100'}
              onChange={(e) => setSettings({ ...settings, admin_ext_update_ext: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="100"
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">ההודעה עולה ל-ivr2:&lt;שלוחה&gt;/M0001.tts, עם פרטי ימות של המערכת.</p>
          </div>

          {settings.admin_ext_last_build && (
            <div className="col-span-1 md:col-span-2 text-xs text-gray-400">
              בנייה אחרונה: {new Date(settings.admin_ext_last_build).toLocaleString('he-IL')}
            </div>
          )}
        </div>
      </div>

      {/* סיכום מוצאי שבת/חג -> הודעה זמנית בימות */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🕯️ סיכום מוצאי שבת/חג</h2>
            <p className="text-sm text-gray-500">
              הודעת הסיכום של ערוץ המקור במוצאי שבת/חג עולה אוטומטית כהודעה זמנית (M0001) בשלוחה, בפורמט TTS וללא צינתוק.
              עוברת סינון Gemini רגיל. החלון נפתח שעה לפני ההבדלה, גם אם המערכת עדיין במצב שבת.
            </p>
          </div>
          <button
            onClick={handleToggleWeekend}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              settings.weekend_summary_enabled === 1 || settings.weekend_summary_enabled === true
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {settings.weekend_summary_enabled === 1 || settings.weekend_summary_enabled === true ? '✅ פעיל' : '⚪ כבוי'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ערוץ מקור</label>
            <input
              type="text"
              value={settings.weekend_summary_source ?? 'amitsegal'}
              onChange={(e) => setSettings({ ...settings, weekend_summary_source: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="amitsegal"
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">שם המשתמש בטלגרם, בלי @. הבוט חייב להיות מנוי על הערוץ.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">מילות מפתח לזיהוי הסיכום</label>
            <input
              type="text"
              value={settings.weekend_summary_keywords ?? 'סיכום'}
              onChange={(e) => setSettings({ ...settings, weekend_summary_keywords: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="סיכום"
            />
            <p className="mt-1 text-xs text-gray-500">מופרדות בפסיק. מספיקה אחת מהן בהודעה. ריק = ההודעה הראשונה של הערוץ בחלון.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">תוקף ההודעה (שעות)</label>
            <input
              type="number"
              min="1"
              value={settings.weekend_summary_hours ?? 2}
              onChange={(e) => setSettings({ ...settings, weekend_summary_hours: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">שלוחה בימות</label>
            <input
              type="text"
              value={settings.weekend_summary_ext ?? '100'}
              onChange={(e) => setSettings({ ...settings, weekend_summary_ext: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="100"
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">ההודעה עולה ל-ivr2:&lt;שלוחה&gt;/M0001.tts.</p>
          </div>

          <div className="md:col-span-2 text-xs text-gray-500">
            החיבור לימות נלקח מהגדרות ימות של המערכת (שם משתמש וסיסמה) — אין צורך להזין כאן טוקן.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button
            onClick={handleSaveWeekend}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-5 h-5 ml-2" />
            שמור
          </button>
          <button
            onClick={handleTestWeekend}
            disabled={testingWeekend}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {testingWeekend ? 'בודק...' : 'בדוק חיבור לימות'}
          </button>
          {settings.weekend_summary_last_at && (
            <span className="text-xs text-gray-500">
              אחרון: {settings.weekend_summary_last_at} — {settings.weekend_summary_last_status}
            </span>
          )}
        </div>
      </div>

      {/* סיכום חדשות אוטומטי */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">📰 סיכום חדשות אוטומטי</h2>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={async () => {
                try {
                  const newValue = !(settings.news_summary_shabbat_mode === 1 || settings.news_summary_shabbat_mode === true);
                  await api.put('/settings', { ...settings, news_summary_shabbat_mode: newValue ? 1 : 0 });
                  setSettings({ ...settings, news_summary_shabbat_mode: newValue ? 1 : 0 });
                  toast.success(newValue ? 'מצב שבת הופעל לסיכום חדשות' : 'מצב שבת בוטל לסיכום חדשות');
                } catch (err) {
                  toast.error('שגיאה בעדכון מצב שבת');
                }
              }}
              className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                settings.news_summary_shabbat_mode === 1 || settings.news_summary_shabbat_mode === true
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title="הפעל/בטל מצב שבת לסיכום חדשות"
            >
              {settings.news_summary_shabbat_mode === 1 || settings.news_summary_shabbat_mode === true ? '🕯️ שבת פעיל' : '🕯️ שבת'}
            </button>
            <button
              onClick={handleRunSummaryNow}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <RefreshCw className="w-5 h-5 ml-2" />
              הפעל עכשיו
            </button>
            <button
              onClick={handleSaveNewsSummary}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-5 h-5 ml-2" />
              שמור
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newsSummary.enabled}
                onChange={(e) => setNewsSummary({ ...newsSummary, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">הפעל סיכום אוטומטי</span>
              <p className="text-sm text-gray-500">הסיכום ישלח לאתר ולימות כל 3 שעות</p>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              שלוחה בימות
            </label>
            <input
              type="text"
              value={newsSummary.extension}
              onChange={(e) => setNewsSummary({ ...newsSummary, extension: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ivr2:telegram/5"
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">
              פורמט: ivr2:נתיב/שלוחה (לדוגמה: ivr2:telegram/5)
            </p>
          </div>

          {newsSummary.last_run && (
            <div className="col-span-1 md:col-span-2">
              <p className="text-sm text-gray-600">
                סיכום אחרון: {new Date(newsSummary.last_run).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}
              </p>
            </div>
          )}
        </div>

        {/* שליחה ל-Black Cat */}
        <div className="mt-4 p-4 bg-orange-50 rounded-lg">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newsSummary.blackcat_enabled}
              onChange={(e) => setNewsSummary({ ...newsSummary, blackcat_enabled: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <div>
              <span className="font-medium text-gray-900">🐱 שליחת סיכום חדשות לחתול השחור</span>
              <p className="text-sm text-gray-500">הסיכום יישלח ככתבה בפורמט Markdown לפי שעות מוגדרות</p>
            </div>
          </label>

          {newsSummary.blackcat_enabled && (
            <div className="mt-3 pl-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                שעות שליחה ל-Black Cat (מופרדות בפסיק)
              </label>
              <input
                type="text"
                value={newsSummary.blackcat_hours}
                onChange={(e) => setNewsSummary({ ...newsSummary, blackcat_hours: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="8,14,20"
                dir="ltr"
              />
              <p className="mt-1 text-xs text-gray-500">
                לדוגמה: 8,14,20 — הסיכום ישלח ל-Black Cat ב-8:00, 14:00 ו-20:00
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">מידע על סיכום החדשות:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• הסיכום נוצר מ-15 ההודעות האחרונות מכל ערוץ פעיל</li>
            <li>• אורך הסיכום: כ-400 מילים עם כותרות לכל נושא</li>
            <li>• הסיכום עובר סינון Gemini לפני העלאה</li>
            <li>• <b>אתר + ימות:</b> הסיכום נשלח כל 3 שעות</li>
            <li>• <b>חתול שחור:</b> הסיכום נשלח לפי שעות מוגדרות</li>
            <li>• <b>הפעל עכשיו:</b> שולח לכל היעדים (אתר + ימות + חתול שחור)</li>
          </ul>
        </div>
      </div>

      {/* חדשות חריגות */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">🚨 חדשות חריגות (Breaking News)</h2>
          <button
            onClick={async () => {
              try {
                await api.put('/settings', {
                  ...settings,
                  breaking_news_enabled: settings.breaking_news_enabled ? 0 : 1
                });
                setSettings({ ...settings, breaking_news_enabled: settings.breaking_news_enabled ? 0 : 1 });
                toast.success(settings.breaking_news_enabled ? 'חדשות חריגות כובו' : 'חדשות חריגות הופעלו');
              } catch (error) {
                toast.error('שגיאה בעדכון');
              }
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              settings.breaking_news_enabled
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {settings.breaking_news_enabled ? '🔴 מופעל' : '⚪ כבוי'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מספר רשימת צינתוקים חינמיים
            </label>
            <input
              type="number"
              value={settings.breaking_ringtone_list || 77}
              onChange={(e) => setSettings({ ...settings, breaking_ringtone_list: parseInt(e.target.value) || 77 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              min="1"
            />
            <p className="mt-1 text-xs text-gray-500">
              מספר הרשימה בצינתוקים חינמיים (ברירת מחדל: 77)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מספר מתקשר 1 (ידיעה דרמטית מאוד)
            </label>
            <input
              type="text"
              dir="ltr"
              value={settings.breaking_caller_1 ?? '0548490988'}
              onChange={(e) => setSettings({ ...settings, breaking_caller_1: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מספר מתקשר 2 (ידיעה פחות דרמטית)
            </label>
            <input
              type="text"
              dir="ltr"
              value={settings.breaking_caller_2 ?? '0747175868'}
              onChange={(e) => setSettings({ ...settings, breaking_caller_2: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              חללי צה"ל: צינתוק מ-X חללים באירוע אחד
            </label>
            <input
              type="number"
              min="1"
              value={settings.breaking_fallen_min ?? 4}
              onChange={(e) => setSettings({ ...settings, breaking_fallen_min: parseInt(e.target.value) || 4 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-500">פחות מזה לא מצנתקים (ברירת מחדל: 4)</p>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">מאילו ערוצים לצנתק</h3>
          {(() => {
            const selected = parseBreakingChannels(settings.breaking_channels);
            const tracked = new Map(allChannels.map(c => [normChannel(c.username), c]));
            const rows = [...BREAKING_DEFAULT_CHANNELS.map(c => ({ ...c, tracked: tracked.has(c.username) }))];
            allChannels.forEach(c => {
              const u = normChannel(c.username);
              if (!rows.some(r => r.username === u)) rows.push({ username: u, name: c.name || u, tracked: true });
            });
            const toggle = (u) => {
              const next = selected.includes(u) ? selected.filter(x => x !== u) : [...selected, u];
              setSettings({ ...settings, breaking_channels: JSON.stringify(next) });
            };
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {rows.map(r => (
                  <label key={r.username} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(r.username)} onChange={() => toggle(r.username)} />
                    <span>{r.name}</span>
                    <span className="text-xs text-gray-400" dir="ltr">{r.username}</span>
                    {!r.tracked && <span className="text-xs text-orange-600">לא מנוטר בבוט</span>}
                  </label>
                ))}
              </div>
            );
          })()}
          <p className="mt-1 text-xs text-gray-500">
            ידיעות מערוץ שלא מסומן כאן לא נבדקות לחדשות חריגות. ערוץ שמסומן "לא מנוטר בבוט" צריך קודם להיות מתווסף בלשונית הערוצים.
          </p>
        </div>

        <div className="mt-6 p-4 border border-gray-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3">📞 שלוחה 8 (חדשות חריגות בטלפון)</h3>
          <p className="text-xs text-gray-500 mb-3">הבוט בונה את <code dir="ltr">telegram/8/Star</code> (ההשמעה) ואת <code dir="ltr">telegram/8/Tz</code> (הרשמה לצינתוקים). לא נוגע ב-<code dir="ltr">telegram/8/ext.ini</code>.</p>
          <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settings.breaking_ext_restrict === undefined || settings.breaking_ext_restrict === null ? true : !!settings.breaking_ext_restrict}
              onChange={(e) => setSettings({ ...settings, breaking_ext_restrict: e.target.checked ? 1 : 0 })}
            />
            <span>רק מי שיכול להיכנס לשלוחת הניהול יכול להקשיב לשלוחה 8 (רשימת התפוצה {settings.admin_ext_whitelist_template || '5'})</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">בלי סימון: השלוחה פתוחה לכולם. בתוך השלוחה, מקש 8 = הרשמה, הסרה או השתקה של צינתוקים (רשימה {settings.breaking_ringtone_list || 77}).</p>
          <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settings.breaking_to_admin === undefined || settings.breaking_to_admin === null ? true : !!settings.breaking_to_admin}
              onChange={(e) => setSettings({ ...settings, breaking_to_admin: e.target.checked ? 1 : 0 })}
            />
            <span>שלח ידיעות חריגות גם לשלוחת הניהול (בורד 99, כולל סרטונים)</span>
          </label>
          <div className="mt-4">
            <button
              onClick={async () => {
                try {
                  await api.put('/settings', {
                    breaking_ext_restrict: settings.breaking_ext_restrict === undefined || settings.breaking_ext_restrict === null ? 1 : (settings.breaking_ext_restrict ? 1 : 0),
                    breaking_to_admin: settings.breaking_to_admin === undefined || settings.breaking_to_admin === null ? 1 : (settings.breaking_to_admin ? 1 : 0)
                  });
                  const res = await api.post('/settings/breaking-ext/build');
                  const data = res.data || {};
                  if (data.success) toast.success('שלוחה 8 נבנתה: ' + (data.log || []).join(' | '));
                  else toast.error('הבנייה נכשלה: ' + (data.error || (data.log || []).join(' | ')));
                } catch (error) {
                  toast.error('שגיאה בבניית שלוחה 8');
                }
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              שמור ובנה / עדכן שלוחה 8
            </button>
            <p className="mt-1 text-xs text-gray-500">נבנה אוטומטית גם כשמפעילים את חדשות חריגות. קובץ ext.ini הקיים נשמר לגיבוי ב-bot/data/ext_backups.</p>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={async () => {
              try {
                await api.put('/settings', {
                  breaking_ringtone_list: settings.breaking_ringtone_list || 77,
                  breaking_caller_1: (settings.breaking_caller_1 ?? '0548490988').trim(),
                  breaking_caller_2: (settings.breaking_caller_2 ?? '0747175868').trim(),
                  breaking_fallen_min: settings.breaking_fallen_min || 4,
                  breaking_channels: JSON.stringify(parseBreakingChannels(settings.breaking_channels))
                });
                toast.success('הגדרות חדשות חריגות נשמרו');
              } catch (error) {
                toast.error('שגיאה בשמירה');
              }
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-5 h-5 ml-2" />
            שמור
          </button>
        </div>

        <div className="mt-4 p-4 bg-red-50 rounded-lg">
          <h3 className="font-semibold text-red-900 mb-2">איך זה עובד:</h3>
          <ul className="text-sm text-red-800 space-y-1">
            <li>• כל ידיעה מהערוצים שסומנו נבדקת ע"י Gemini ומסווגת לסוג אירוע (שיגורים, פיגועים, אסונות, חריג פוליטי ועוד)</li>
            <li>• הטבלה שקובעת מה מצנתק ומאיזה מספר נמצאת ב-<code>bot/breaking_rules.py</code> (ברירת מחדל לפי מפת הצינתוקים)</li>
            <li>• ידיעה דרמטית מאוד: צינתוק מ-{settings.breaking_caller_1 || '0548490988'}. פחות דרמטית: מ-{settings.breaking_caller_2 || '0747175868'}. הרשימה: {settings.breaking_ringtone_list || 77}</li>
            <li>• אירוע אחד מצנתק פעם אחת. רק ידיעה שצונתק עליה נכנסת לשלוחה 8 (עדכונים וכפילויות לא)</li>
            <li>• אם חזרה הסלמה באירוע שכבר צונתק: צינתוק נוסף, וההודעה בשלוחה 8 נפתחת ב"חזרה הסלמה"</li>
            <li>• חללי צה"ל: מצנתקים רק כשיש {settings.breaking_fallen_min ?? 4} חללים לפחות באירוע אחד</li>
            <li>• מגבלה: לא יותר מ-3 צינתוקים בשעה. ידיעה שנחסמה בגלל המגבלה עדיין נכנסת לשלוחה 8, בלי צינתוק</li>
            <li>• חדשות חריגות נשלחות תמיד (גם בשבת)</li>
          </ul>
        </div>
      </div>

      {/* ניהול Webhooks */}
      <WebhooksManager />

      {/* אינטגרציה עם TheChannel */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            <Film className="inline-block w-6 h-6 ml-2" />
            אינטגרציה עם TheChannel
          </h2>
          <button
            onClick={handleSaveSettings}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-5 h-5 ml-2" />
            שמור
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              כתובת Webhook של TheChannel
            </label>
            <input
              type="url"
              value={settings.thechannel_webhook_url || ''}
              onChange={(e) => setSettings({ ...settings, thechannel_webhook_url: e.target.value })}
              placeholder="https://your-site.com/bot/webhook"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מפתח API (אופציונלי)
            </label>
            <input
              type="text"
              value={settings.thechannel_api_key || ''}
              onChange={(e) => setSettings({ ...settings, thechannel_api_key: e.target.value })}
              placeholder="הזן מפתח API..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              dir="ltr"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.thechannel_send_draft === 1 || settings.thechannel_send_draft === true}
              onChange={(e) => setSettings({ ...settings, thechannel_send_draft: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">
              שלח כטיוטה (draft) - הודעות יישמרו אבל לא יוצגו עד שמפעילים סינון ערוצים
            </span>
          </label>
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.website_duplicate_check_enabled === 1 || settings.website_duplicate_check_enabled === true || settings.website_duplicate_check_enabled === undefined}
              onChange={(e) => setSettings({ ...settings, website_duplicate_check_enabled: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">
              בדיקת כפילויות לאתר - ידיעה שכבר דווחה בערוץ אחר תישלח מוסתרת (מוצגת רק בסינון הערוץ שלה). כיבוי חוסך קריאות Gemini
            </span>
          </label>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">איך זה עובד:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• כל הודעה חדשה תישלח ל-TheChannel בפורמט מובנה</li>
            <li>• כולל: שם ערוץ, תוכן, תמונה, סרטון, קטגוריה</li>
            <li>• סרטונים יועלו אוטומטית לתיקיית uploads/videos</li>
            <li>• אם "שלח כטיוטה" מסומן - הודעות יישמרו אך לא יוצגו בראשי</li>
          </ul>
        </div>
      </div>

      {/* הגדרות בוט מתקדמות - יעד חודשי, התראות, pause, מבצע היום */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-indigo-600" />
            הגדרות בוט מתקדמות לאלי אקספרס
          </h2>
          <button
            onClick={handleSaveSettings}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-5 h-5 ml-2" />
            שמור
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* יעד חודשי */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              יעד עמלות חודשי ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.monthly_goal ?? 100}
              onChange={(e) => setSettings({ ...settings, monthly_goal: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              dir="ltr"
            />
            <p className="text-xs text-gray-500 mt-1">מוצג כבר התקדמות באנליטיקה</p>
          </div>

          {/* Webhook להתראות */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              קישור Webhook להתראות מערכת
            </label>
            <input
              type="url"
              value={settings.admin_alert_webhook || ''}
              onChange={(e) => setSettings({ ...settings, admin_alert_webhook: e.target.value })}
              placeholder="https://chat.googleapis.com/v1/spaces/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              dir="ltr"
            />
            <p className="text-xs text-gray-500 mt-1">התראות על שגיאות, קניות חדשות ומבצע היום יישלחו לכאן</p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {/* מצב Pause */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.bot_paused === 1 || settings.bot_paused === true}
              onChange={(e) => setSettings({ ...settings, bot_paused: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <span className="font-medium text-gray-900">מצב חירום (Pause)</span>
              <p className="text-sm text-gray-500">הבוט יפסיק לשלוח הודעות אך ימשיך לקלוט ולסנן</p>
            </div>
          </label>

          {/* מבצע היום */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.daily_deal_enabled === 1 || settings.daily_deal_enabled === true}
              onChange={(e) => setSettings({ ...settings, daily_deal_enabled: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">מבצע היום אוטומטי</span>
              <p className="text-sm text-gray-500">הבוט יבחר מוצר אקראי מדי יום ויקדם אותו כ"מבצע היום"</p>
            </div>
          </label>

          {/* מצב בדיקה למבצע היום */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-yellow-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.daily_deal_dry_run === 1 || settings.daily_deal_dry_run === true}
              onChange={(e) => setSettings({ ...settings, daily_deal_dry_run: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">מבצע היום - מצב בדיקה (לא שולח)</span>
              <p className="text-sm text-gray-500">מדפיס ללוגים בלבד ולא שולח ליעדים / וובהוקים</p>
            </div>
          </label>

          {/* שעת מבצע היום */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שעת מבצע היום (שעה ביום, 0-23)
              </label>
              <p className="text-sm text-gray-500">באיזו שעה הבוט יפרסם את מבצע היום</p>
            </div>
            <input
              type="number"
              min="0"
              max="23"
              value={settings.daily_deal_hour ?? 10}
              onChange={(e) => setSettings({ ...settings, daily_deal_hour: parseInt(e.target.value) || 10 })}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center"
              dir="ltr"
            />
          </div>

          {/* כפתור קדם מוצר עכשיו + מצב שבת */}
          <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
            <div className="flex-1">
              <span className="font-medium text-gray-900">קדם מוצר עכשיו</span>
              <p className="text-sm text-gray-500">בחר מוצר אקראי מהמאושרים וקדם אותו כמבצע היום מיד</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const newValue = !(settings.daily_deal_shabbat_mode === 1 || settings.daily_deal_shabbat_mode === true);
                  await api.put('/settings', { ...settings, daily_deal_shabbat_mode: newValue ? 1 : 0 });
                  setSettings({ ...settings, daily_deal_shabbat_mode: newValue ? 1 : 0 });
                  toast.success(newValue ? 'מצב שבת הופעל למבצע היום' : 'מצב שבת בוטל למבצע היום');
                } catch (err) {
                  toast.error('שגיאה בעדכון מצב שבת');
                }
              }}
              className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                settings.daily_deal_shabbat_mode === 1 || settings.daily_deal_shabbat_mode === true
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title="הפעל/בטל מצב שבת למבצע היום"
            >
              {settings.daily_deal_shabbat_mode === 1 || settings.daily_deal_shabbat_mode === true ? '🕯️ שבת פעיל' : '🕯️ שבת'}
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.post('/aliexpress/promote-deal');
                  toast.success(res.data.message || 'מבצע היום נשלח!');
                } catch (err) {
                  toast.error(err.response?.data?.error || 'שגיאה בקידום מוצר');
                }
              }}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
            >
              <Gift className="w-4 h-4 ml-2" />
              קדם עכשיו
            </button>
          </div>

          {/* שליחת מבצע היום ל-Black Cat */}
          <div className="p-3 bg-purple-50 rounded-lg space-y-3">
            <div>
              <span className="font-medium text-gray-900">שליחת מבצע היום ל-Black Cat</span>
              <p className="text-sm text-gray-500">מבצע היום יישלח גם לכתובת הזו בנוסף ליעדים הרגילים</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">כתובת API</label>
              <input
                type="url"
                value={settings.blackcat_url || ''}
                onChange={(e) => setSettings({ ...settings, blackcat_url: e.target.value })}
                placeholder="https://black-cat.thechats.click/api/import/post"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                dir="ltr"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">מפתח API (X-API-Key)</label>
              <input
                type="text"
                value={settings.blackcat_api_key || ''}
                onChange={(e) => setSettings({ ...settings, blackcat_api_key: e.target.value })}
                placeholder="crjbhi"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                dir="ltr"
              />
            </div>
          </div>

          {/* בדיקת הזמנות אוטומטית */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.order_polling_enabled === 1 || settings.order_polling_enabled === true}
              onChange={(e) => setSettings({ ...settings, order_polling_enabled: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <div>
              <span className="font-medium text-gray-900">בדיקת הזמנות אוטומטית</span>
              <p className="text-sm text-gray-500">הבוט יבדוק אם נרכשו מוצרים חדשים וישלח התראות על קניות</p>
            </div>
          </label>

          {/* תדירות בדיקת הזמנות */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תדירות בדיקת הזמנות (דקות)
              </label>
              <p className="text-sm text-gray-500">כל כמה דקות הבוט יבדוק הזמנות חדשות</p>
            </div>
            <input
              type="number"
              min="5"
              max="1440"
              value={settings.order_polling_interval ?? 15}
              onChange={(e) => setSettings({ ...settings, order_polling_interval: parseInt(e.target.value) || 15 })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-center"
              dir="ltr"
            />
          </div>
        </div>
      </div>

      {/* הגדרות אבטחה */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-green-600" />
            אבטחה
          </h2>
        </div>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">אימות דו-שלבי (TOTP)</p>
            <p className="text-sm text-gray-500">Google Authenticator או אפליקציית אימות דומה</p>
          </div>
          <button
            onClick={() => setShowTotpModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            הגדר
          </button>
        </div>
      </div>

      {showTotpModal && <TotpSetup onClose={() => setShowTotpModal(false)} />}

      {/* ניהול סרטונים */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">ניהול סרטונים</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-2">
              <label htmlFor="videoSort" className="text-sm text-gray-600">סידור לפי:</label>
              <select
                id="videoSort"
                value={videoSort}
                onChange={(e) => setVideoSort(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="date_desc">מהחדש לישן</option>
                <option value="date_asc">מהישן לחדש</option>
                <option value="size_desc">גודל: גדול לקטן</option>
                <option value="size_asc">גודל: קטן לגדול</option>
              </select>
            </div>
            <button
              onClick={() => handleCleanupVideos(30)}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <Trash2 className="w-5 h-5 ml-2" />
              מחק +30 ימים
            </button>
            <button
              onClick={handleCleanupAll}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="w-5 h-5 ml-2" />
              מחק הכל
            </button>
            <button
              onClick={loadVideos}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <RefreshCw className="w-5 h-5 ml-2" />
              רענן
            </button>
          </div>
        </div>

        <div className="mb-4 text-sm text-gray-600">
          סה"כ: {videos.length} סרטונים ({formatBytes(videos.reduce((sum, v) => sum + v.size, 0))})
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedVideos.map((video) => {
            const videoUrl = `/videos/${encodeURIComponent(video.filename)}`;

            return (
              <div key={video.filename} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {video.filename}
                </div>
                <div className="text-xs text-gray-500">
                  {formatBytes(video.size)} • {new Date(video.created).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                </div>
                <div className="flex gap-2">
                  <a
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    צפייה
                  </a>
                  <button
                    onClick={() => handleDeleteVideo(video.filename)}
                    className="flex-1 flex items-center justify-center px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    מחק
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {videos.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            אין סרטונים
          </div>
        )}
      </div>
    </div>
  );
}
